"use strict";
/**
 * Utilidades de data/hora.
 *
 * O servidor persiste tudo em UTC; grupos têm fuso próprio (default
 * America/Sao_Paulo). As funções que montam horários recorrentes recebem o
 * fuso explicitamente para não depender do relógio do processo.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.DEFAULT_TIMEZONE = void 0;
exports.zonedTimeToUtc = zonedTimeToUtc;
exports.formatDateISO = formatDateISO;
exports.formatTime = formatTime;
exports.weekdayIn = weekdayIn;
exports.nextWeekdayOccurrence = nextWeekdayOccurrence;
exports.addDaysToISO = addDaysToISO;
exports.addMinutes = addMinutes;
exports.addHours = addHours;
exports.addDays = addDays;
exports.referenceMonth = referenceMonth;
exports.addMonthsToReference = addMonthsToReference;
exports.dueDateFor = dueDateFor;
exports.effectiveDueDate = effectiveDueDate;
exports.weekdayLabel = weekdayLabel;
exports.relativeDayLabel = relativeDayLabel;
exports.isPast = isPast;
exports.hoursBetween = hoursBetween;
exports.daysBetween = daysBetween;
const types_1 = require("@nossorachao/types");
exports.DEFAULT_TIMEZONE = 'America/Sao_Paulo';
const MS_PER_MINUTE = 60_000;
const MS_PER_HOUR = 3_600_000;
const MS_PER_DAY = 86_400_000;
/** Deslocamento (ms) de um fuso IANA em relação ao UTC num dado instante. */
function timezoneOffsetMs(date, timeZone) {
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hour12: false,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });
    const parts = Object.fromEntries(formatter.formatToParts(date).map((part) => [part.type, part.value]));
    const asUtc = Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day), Number(parts.hour === '24' ? '0' : parts.hour), Number(parts.minute), Number(parts.second));
    return asUtc - date.getTime();
}
/**
 * Monta o instante UTC correspondente a uma data/hora local de um fuso.
 * Ex.: `zonedTimeToUtc('2026-08-17', '20:00', 'America/Sao_Paulo')`.
 */
function zonedTimeToUtc(dateISO, time, timeZone) {
    const [year, month, day] = dateISO.split('-').map(Number);
    const [hour, minute] = time.split(':').map(Number);
    const naive = Date.UTC(year ?? 0, (month ?? 1) - 1, day ?? 1, hour ?? 0, minute ?? 0, 0);
    // Duas passagens resolvem a virada de horário de verão na borda.
    const firstGuess = new Date(naive - timezoneOffsetMs(new Date(naive), timeZone));
    return new Date(naive - timezoneOffsetMs(firstGuess, timeZone));
}
/** Data no formato YYYY-MM-DD conforme o fuso informado. */
function formatDateISO(date, timeZone = exports.DEFAULT_TIMEZONE) {
    return new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(date);
}
/** Hora no formato HH:mm conforme o fuso informado. */
function formatTime(date, timeZone = exports.DEFAULT_TIMEZONE) {
    return new Intl.DateTimeFormat('pt-BR', {
        timeZone,
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).format(date);
}
/** Dia da semana (0=domingo) conforme o fuso informado. */
function weekdayIn(date, timeZone = exports.DEFAULT_TIMEZONE) {
    const label = new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'short' }).format(date);
    const map = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
    return map[label] ?? 0;
}
/**
 * Próxima ocorrência de um dia da semana + horário, a partir de `from`.
 * Se hoje é o dia e o horário ainda não passou, retorna hoje.
 */
function nextWeekdayOccurrence(weekday, time, timeZone = exports.DEFAULT_TIMEZONE, from = new Date()) {
    const todayISO = formatDateISO(from, timeZone);
    const currentWeekday = weekdayIn(from, timeZone);
    let daysAhead = (weekday - currentWeekday + 7) % 7;
    let candidate = zonedTimeToUtc(addDaysToISO(todayISO, daysAhead), time, timeZone);
    if (candidate.getTime() <= from.getTime()) {
        daysAhead += 7;
        candidate = zonedTimeToUtc(addDaysToISO(todayISO, daysAhead), time, timeZone);
    }
    return candidate;
}
/** Soma dias a uma data YYYY-MM-DD, devolvendo YYYY-MM-DD. */
function addDaysToISO(dateISO, days) {
    const [year, month, day] = dateISO.split('-').map(Number);
    const base = Date.UTC(year ?? 0, (month ?? 1) - 1, day ?? 1);
    return new Date(base + days * MS_PER_DAY).toISOString().slice(0, 10);
}
function addMinutes(date, minutes) {
    return new Date(date.getTime() + minutes * MS_PER_MINUTE);
}
function addHours(date, hours) {
    return new Date(date.getTime() + hours * MS_PER_HOUR);
}
function addDays(date, days) {
    return new Date(date.getTime() + days * MS_PER_DAY);
}
/** Competência YYYY-MM de uma data. */
function referenceMonth(date, timeZone = exports.DEFAULT_TIMEZONE) {
    return formatDateISO(date, timeZone).slice(0, 7);
}
/** Soma meses a uma competência YYYY-MM. */
function addMonthsToReference(reference, months) {
    const [year, month] = reference.split('-').map(Number);
    const total = (year ?? 0) * 12 + ((month ?? 1) - 1) + months;
    const newYear = Math.floor(total / 12);
    const newMonth = (total % 12) + 1;
    return `${String(newYear).padStart(4, '0')}-${String(newMonth).padStart(2, '0')}`;
}
/**
 * Data de vencimento de uma mensalidade.
 * Fixa em 28 no máximo (ver `FINANCE.MAX_DUE_DAY`), então não há mês curto.
 */
function dueDateFor(reference, dueDay, timeZone = exports.DEFAULT_TIMEZONE) {
    const clamped = Math.min(Math.max(dueDay, 1), 28);
    return zonedTimeToUtc(`${reference}-${String(clamped).padStart(2, '0')}`, '23:59', timeZone);
}
/**
 * Vencimento de uma mensalidade gerada agora.
 *
 * Normalmente é o dia de vencimento do grupo. Se esse dia já passou — o mês
 * foi gerado tarde —, vira `lateDays` dias a partir de hoje: ninguém pode
 * receber uma cobrança que já chega atrasada.
 */
function effectiveDueDate(reference, dueDay, lateDays, timeZone = exports.DEFAULT_TIMEZONE, now = new Date()) {
    const regular = dueDateFor(reference, dueDay, timeZone);
    if (regular.getTime() >= now.getTime())
        return regular;
    const late = formatDateISO(new Date(now.getTime() + lateDays * MS_PER_DAY), timeZone);
    return zonedTimeToUtc(late, '23:59', timeZone);
}
function weekdayLabel(weekday, short = false) {
    return (short ? types_1.WEEKDAY_SHORT_PT[weekday] : types_1.WEEKDAY_LABELS_PT[weekday]) ?? '';
}
/** Rótulo humano relativo: "hoje", "amanhã", "em 3 dias", "há 2 dias". */
function relativeDayLabel(date, from = new Date(), timeZone = exports.DEFAULT_TIMEZONE) {
    const target = formatDateISO(date, timeZone);
    const base = formatDateISO(from, timeZone);
    const diffDays = Math.round((Date.parse(target) - Date.parse(base)) / MS_PER_DAY);
    if (diffDays === 0)
        return 'hoje';
    if (diffDays === 1)
        return 'amanhã';
    if (diffDays === -1)
        return 'ontem';
    if (diffDays > 1 && diffDays <= 7)
        return `em ${diffDays} dias`;
    if (diffDays < -1 && diffDays >= -7)
        return `há ${Math.abs(diffDays)} dias`;
    return new Intl.DateTimeFormat('pt-BR', { timeZone, day: '2-digit', month: 'short' }).format(date);
}
function isPast(date, from = new Date()) {
    return date.getTime() < from.getTime();
}
function hoursBetween(a, b) {
    return (b.getTime() - a.getTime()) / MS_PER_HOUR;
}
function daysBetween(a, b) {
    return (b.getTime() - a.getTime()) / MS_PER_DAY;
}
//# sourceMappingURL=date.js.map